import { Link } from 'react-router-dom';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useIsHongKongIp } from '../../hooks/useIsHongKongIp';
import { normalizeSiteLanguage } from '../../utils/currentSiteLanguage';
import './site-header.css';

export default function Header() {
  const { t, i18n } = useTranslation();
  const language = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const isChinese = language === 'hk' || language === 'cn';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const { canShowRestrictedContent } = useIsHongKongIp();

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;

    // Measure only the navigation bar; opening the overlay menu must not move the hero.
    const updateHeight = () => {
      const height = Math.ceil(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--site-header-height', `${height}px`);
    };
    updateHeight();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateHeight);
    observer?.observe(bar);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.removeProperty('--site-header-height');
    };
  }, []);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1280px)');
    const closeOnDesktop = () => {
      if (desktop.matches) setIsMenuOpen(false);
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <>
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/55 backdrop-blur-sm xl:hidden"
          onClick={closeMenu}
        />
      )}

      <header
        className={`site-header fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isMenuOpen
            ? 'bg-black/85 backdrop-blur-xl shadow-2xl border-b border-orange-400/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={barRef} className="flex justify-between items-center gap-3 py-3 md:py-4">
            <Link to="/" onClick={closeMenu} className={`site-header__brand shrink-0${isChinese ? '' : ' md:me-4'}`}>
              <div className="site-header__brand-content flex items-center gap-3 group cursor-pointer">
                <div className="site-header__logo w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shrink-0">
                  <img
                    src="/img/0c35285da7db83dd4744dcd7f9c41cc3.png"
                    alt={isChinese ? '金利寶環球 Gold Label Global Limited logo' : 'Gold Label Global logo'}
                    className="w-full h-full object-contain filter drop-shadow-lg group-hover:drop-shadow-2xl transition-all duration-300"
                  />
                </div>

                <div className="leading-tight">
                  {isChinese ? (
                    <>
                      <span
                        className="site-header__brand-title text-lg sm:text-xl md:text-2xl font-semibold text-white tracking-wide block"
                        style={{ fontFamily: '"Times New Roman", serif' }}
                      >
                        GOLD LABEL
                      </span>
                      <span className="text-xs md:text-sm font-semibold text-orange-300 tracking-wider block">
                        金利寶環球
                      </span>
                    </>
                  ) : (
                    <span
                      dir="ltr"
                      className="text-base sm:text-lg md:text-xl font-semibold text-white tracking-wide whitespace-nowrap block"
                      style={{ fontFamily: '"Times New Roman", serif' }}
                    >
                      Gold Label
                      <br />
                      Global
                    </span>
                  )}
                </div>
              </div>
            </Link>

            <div className="hidden min-w-0 flex-1 flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm xl:flex">
              <Link
                to="/"
                className="text-white hover:text-orange-400 font-medium transition-colors cursor-pointer"
              >
                {t('nav.home')}
              </Link>

              <Link
                to="/about"
                className="text-white hover:text-orange-400 font-medium transition-colors cursor-pointer"
              >
                {t('nav.about')}
              </Link>

              <Link
                to="/license"
                className="text-white hover:text-orange-400 font-medium transition-colors cursor-pointer"
              >
                {t('nav.license')}
              </Link>

              {canShowRestrictedContent && (
                <>
                  <Link
                    to="/platform"
                    className="text-white hover:text-orange-400 font-medium transition-colors cursor-pointer"
                  >
                    {t('nav.trading')}
                  </Link>

                  <Link
                    to="/market"
                    className="text-white hover:text-orange-400 font-medium transition-colors cursor-pointer"
                  >
                    {t('nav.markets')}
                  </Link>
                </>
              )}

              <Link
                to="/grand-opening"
                className="text-white hover:text-orange-400 font-medium transition-colors cursor-pointer"
              >
                {t('nav.news')}
              </Link>

              <Link
                to="/contact"
                className="text-white hover:text-orange-400 font-medium transition-colors cursor-pointer"
              >
                {t('nav.contact')}
              </Link>
            </div>

            <div className="flex shrink-0 items-center gap-3 md:gap-4">
              <LanguageSwitcher />

              {canShowRestrictedContent && (
                <>
                  <Link to="https://portal.jlbhk.com/#/user/login" className="hidden xl:block">
                    <button className="hidden xl:block text-white hover:text-orange-400 transition-all duration-300 font-medium px-4 py-2 border border-white/25 rounded-xl hover:border-orange-400 hover:bg-orange-400/10 transform hover:scale-105">
                      {t('nav.login')}
                    </button>
                  </Link>

                  <Link to="https://portal.jlbhk.com/regist-real" className="hidden xl:block">
                    <button className="hidden xl:block bg-gradient-to-r from-orange-400 to-orange-600 text-white font-semibold px-6 py-2 rounded-xl hover:from-orange-500 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-orange-400/40">
                      {t('nav.register')}
                    </button>
                  </Link>
                </>
              )}

              <button
                onClick={() => setIsMenuOpen((prev) => !prev)}
                className="xl:hidden w-10 h-10 flex items-center justify-center text-white rounded-lg hover:bg-white/10 hover:text-orange-400 transition-all duration-300"
                aria-label={t('nav.toggleMenu', { defaultValue: 'Toggle menu' })}
                aria-expanded={isMenuOpen}
                aria-controls="site-mobile-navigation"
              >
                <i
                  className={`text-2xl transition-all duration-300 ${
                    isMenuOpen ? 'ri-close-line rotate-180' : 'ri-menu-line'
                  }`}
                />
              </button>
            </div>
          </div>

          <div
            id="site-mobile-navigation"
            data-open={isMenuOpen}
            aria-hidden={!isMenuOpen}
            inert={!isMenuOpen}
            className={`site-header__menu xl:hidden transition-all duration-300 ${
              isMenuOpen ? 'opacity-100 pb-4' : 'opacity-0'
            }`}
          >
            <nav className="mt-2 rounded-2xl border border-white/10 bg-black/80 backdrop-blur-2xl shadow-2xl p-3">
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={closeMenu}
                  className="block text-white hover:text-orange-400 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-orange-400/10"
                >
                  {t('nav.home')}
                </Link>

                <Link
                  to="/about"
                  onClick={closeMenu}
                  className="block text-white hover:text-orange-400 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-orange-400/10"
                >
                  {t('nav.about')}
                </Link>

                <Link
                  to="/license"
                  onClick={closeMenu}
                  className="block text-white hover:text-orange-400 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-orange-400/10"
                >
                  {t('nav.license')}
                </Link>

                {canShowRestrictedContent && (
                  <>
                    <Link
                      to="/platform"
                      onClick={closeMenu}
                      className="block text-white hover:text-orange-400 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-orange-400/10"
                    >
                      {t('nav.trading')}
                    </Link>

                    <Link
                      to="/market"
                      onClick={closeMenu}
                      className="block text-white hover:text-orange-400 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-orange-400/10"
                    >
                      {t('nav.markets')}
                    </Link>
                  </>
                )}

                <Link
                  to="/grand-opening"
                  onClick={closeMenu}
                  className="block text-white hover:text-orange-400 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-orange-400/10"
                >
                  {t('nav.news')}
                </Link>

                <Link
                  to="/contact"
                  onClick={closeMenu}
                  className="block text-white hover:text-orange-400 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-orange-400/10"
                >
                  {t('nav.contact')}
                </Link>
              </div>

              {canShowRestrictedContent && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex gap-3">
                    <Link
                      to="https://portal.jlbhk.com/#/user/login"
                      className="flex-1"
                      onClick={closeMenu}
                    >
                      <button className="w-full text-white font-medium px-4 py-3 border border-white/25 rounded-xl hover:border-orange-400 hover:text-orange-400 hover:bg-orange-400/10 transition-all duration-300">
                        {t('nav.login')}
                      </button>
                    </Link>

                    <Link
                      to="https://portal.jlbhk.com/regist-real"
                      className="flex-1"
                      onClick={closeMenu}
                    >
                      <button className="w-full bg-gradient-to-r from-orange-400 to-orange-600 text-white font-semibold px-4 py-3 rounded-xl hover:from-orange-500 hover:to-orange-700 transition-all duration-300 shadow-lg">
                        {t('nav.register')}
                      </button>
                    </Link>
                  </div>
                </div>
              )}
            </nav>
          </div>
        </div>
      </header>
    </>
  );
}
